import React, { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppSpin } from '@/components/atoms';

import PatientAddressFormBottomBar from '../components/PatientAddressFormBottomBar';
import PatientPageHeader from '@/pages/main/patient/components/PatientPageHeader';
import { useMainPatient } from '@/hooks/main/useMainPatient';
import {
  usePatientAddressQuery,
  usePatientAddressUpdateMutation,
} from '@/hooks/main/usePatientAddresses';
import PatientAddressForm from '../components/PatientAddressForm';
import type { PatientAddressFormData } from '@/schemas/patientAddressSchema';
import { NotFound } from '@/pages/main/system/NotFound';
import { authorizeNavigation } from '@/hooks/useFormConfirmationBlocker';

const EditPatientAddressPage: React.FC = () => {
  const { id, addressId, tenant } = useParams<{
    id: string;
    addressId: string;
    tenant?: string;
  }>();
  const navigate = useNavigate();
  const patientId = id ? parseInt(id, 10) : null;
  const addressIdNum = addressId ? parseInt(addressId, 10) : null;

  const { data: patient, isLoading: patientLoading } = useMainPatient(patientId ?? undefined);
  const { data: addressDetail, isLoading: detailLoading } = usePatientAddressQuery(
    patientId,
    addressIdNum
  );
  const { updateMutation, isLoading: isUpdating } = usePatientAddressUpdateMutation();

  const handleBack = useCallback(() => {
    navigate(`/${tenant}/patient_addresses/${id}/list`);
  }, [navigate, tenant, id]);

  const handleSubmit = useCallback(
    (data: PatientAddressFormData) => {
      if (!patientId || !addressIdNum) return;
      return updateMutation({ patientId, addressId: addressIdNum, data }).then(() => {
        authorizeNavigation();
        navigate(`/${tenant}/patient_addresses/${id}/list`);
      });
    },
    [patientId, addressIdNum, updateMutation, navigate, tenant, id]
  );

  const defaultValues = useMemo((): Partial<PatientAddressFormData> | undefined => {
    if (!addressDetail) return undefined;
    return {
      postal_code1: addressDetail.postal_code1 ?? '',
      postal_code2: addressDetail.postal_code2 ?? '',
      address1: addressDetail.address1 ?? '',
      address2: addressDetail.address2 ?? '',
      address3: addressDetail.address3 ?? '',
      name: addressDetail.name ?? '',
      address2_hidden: addressDetail.address2_hidden,
      is_facility_resident: addressDetail.is_facility_resident,
      address3_check: addressDetail.address3_check,
      is_special_area_addition: addressDetail.is_special_area_addition,
      is_default: addressDetail.is_default,
    };
  }, [addressDetail]);

  if (patientId === null || isNaN(patientId) || addressIdNum === null || isNaN(addressIdNum)) {
    return <NotFound />;
  }

  if ((patientLoading && !patient) || detailLoading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <AppSpin size="large" />
      </div>
    );
  }

  if (!patient || !addressDetail) {
    return <NotFound />;
  }

  const formId = 'patient-address-edit-form';

  return (
    <div>
      <PatientPageHeader
        patient={patient}
        patientId={id!}
        tenant={tenant}
        navigate={navigate}
        activeKey="patient-addresses"
        disabled={isUpdating}
      />

      <PatientAddressForm
        key={addressIdNum}
        formId={formId}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
      />

      <PatientAddressFormBottomBar
        mode="edit"
        isSubmitting={isUpdating}
        formId={formId}
        onBack={handleBack}
      />
    </div>
  );
};

export default EditPatientAddressPage;
